import React, { useMemo, useState } from 'react';

import type {
  AlineSelection,
  PageDimensions,
  RemoteSelection,
  RenderModelDocument,
  RenderModelPage,
} from '@aline/common';
import { inToPx, pageSeparationPx, Select } from '@aline/common';
import { useDroppable } from '@dnd-kit/core';
import { equals } from 'ramda';
import { Path } from 'slate';
import type { DropField } from 'src/components';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { DocumentContextMenu } from 'src/editor/toolbars';
import { VerticalRuler } from 'src/editor/toolbars/rulers/VerticalRuler';
import { domElIdCreators, dropIds, useIsAtVersionHistoryRoute } from 'src/helpers';
import { PageElContext, useEditor } from 'src/layout-engine';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { makeStyles } from 'tss-react/mui';
import { useDeepCompareMemo } from 'use-deep-compare';

import { Caret } from './Caret';
import { DebugOverlay } from './DebugOverlay';
import { DrawReactComponent } from './DrawReactComponent';
import { HeaderOrFooter, useOpenHeaderOrFooterPageNum } from './HeaderOrFooter';
import { groupAnnotationMarkers, PageAnnotationMarker } from './PageAnnotationMarker';
import { RenderingError } from './RenderingError';
import { usePageCanvas } from '../hooks/use-page-canvas';
import type { ReactDrawInstruction } from '../hooks/use-page-draw-instructions';
import { usePageDrawInstructions } from '../hooks/use-page-draw-instructions';
import { usePageEventHandlers } from '../hooks/use-page-event-handlers';

interface StyleProps {
  activePageDimensions: PageDimensions;
}

const useStyles = makeStyles<StyleProps>()((theme, { activePageDimensions }) => ({
  page: {
    backgroundColor: 'white',
    boxShadow: theme.appShadows.page,
    boxSizing: 'border-box',
    cursor: 'text',
    height: inToPx(activePageDimensions.height),
    margin: '0 auto',
    /** Counteract React Window trying to disable pointer events while scrolling. */
    pointerEvents: 'auto',
    position: 'relative',
    userSelect: 'none',
    width: inToPx(activePageDimensions.width),
  },
  pageContent: {
    inset: 0,
    position: 'absolute',
  },
  root: {
    paddingTop: pageSeparationPx,
    position: 'relative',
  },
}));

export interface PageProps {
  activePageDimensions: PageDimensions;
  page: RenderModelPage;
  pageNumber: number;
  path: Path;
  rMDoc: RenderModelDocument;
  rMRemoteSelections: Record<string, RemoteSelection[]>;
  rMSelections: AlineSelection[] | null;
}

export const Page = React.memo(function Page(props: PageProps): JSX.Element {
  const { activePageDimensions, page, pageNumber, path, rMDoc, rMSelections } = props;

  const tabId = useCurrentTabIdOrThrow();
  const activeHeaderOrFooterPageNum = useOpenHeaderOrFooterPageNum();
  const [pageEl, setPageEl] = useState<HTMLDivElement | null>(null);

  const { contextMenuLocation, eventHandlers, handleMouseDown, setContextMenuLocation } =
    usePageEventHandlers({
      pageEl,
      rMPageEntry: [page, path],
    });

  const isFocusOnPage = useMemo(() => {
    if (!rMSelections) return false;
    if (activeHeaderOrFooterPageNum === pageNumber) return true;
    return rMSelections.some(rMSelection => Path.isDescendant(rMSelection.focus.path, path));
  }, [activeHeaderOrFooterPageNum, pageNumber, path, rMSelections]);

  const { classes } = useStyles({ activePageDimensions });

  /**
   * Override the mouse down behavior for headers and footers to prevent scroll changes while
   * attempting to double-click.
   */
  function handleMouseDownHeaderOrFooter(event: React.MouseEvent) {
    event.stopPropagation();
    handleMouseDown(event, { skipScrollSelectionIntoView: true });
  }

  function handleDoubleClickHeaderOrFooter(event: React.MouseEvent) {
    handleMouseDown(event, { dispatchClick: false, forceAllowHeaderAndFooter: true });
  }

  return (
    <PageElContext.Provider value={pageEl}>
      <div className={classes.root}>
        <div
          ref={setPageEl}
          data-page
          className={classes.page}
          data-page-number={pageNumber}
          data-page-path={JSON.stringify(path)}
          id={domElIdCreators.pageEl(tabId, pageNumber)}
          {...eventHandlers}
        >
          <HeaderOrFooter
            pageNumber={pageNumber}
            rMDoc={rMDoc}
            type="header"
            onDoubleClick={handleDoubleClickHeaderOrFooter}
            onMouseDown={handleMouseDownHeaderOrFooter}
          />

          <PageContent {...props} className={classes.pageContent} />

          <HeaderOrFooter
            pageNumber={pageNumber}
            rMDoc={rMDoc}
            type="footer"
            onDoubleClick={handleDoubleClickHeaderOrFooter}
            onMouseDown={handleMouseDownHeaderOrFooter}
          />

          <DebugOverlay rMElementEntry={[page, path]} />
        </div>

        <VerticalRuler active={isFocusOnPage} />
      </div>

      <DocumentContextMenu location={contextMenuLocation} setLocation={setContextMenuLocation} />
    </PageElContext.Provider>
  );
}, equals);

interface PageContentProps extends PageProps {
  className?: string;
}

function PageContent({
  activePageDimensions,
  className,
  page,
  pageNumber,
  path,
  rMRemoteSelections,
  rMSelections,
}: PageContentProps) {
  const editor = useEditor();
  const tabId = useCurrentTabIdOrThrow();
  const { isAtVersionHistoryRoute } = useIsAtVersionHistoryRoute();
  const suggestedEdits = useEditorTabStateSelector(state => state.derived.suggestedEdits, equals);

  const pageWidthPx = inToPx(activePageDimensions.width);
  const pageHeightPx = inToPx(activePageDimensions.height);

  const { setNodeRef } = useDroppable({
    data: { pageNumber, rMPageEntry: [page, path], tabId } satisfies DropField,
    id: dropIds.alinePage(tabId, path),
  });

  const { annotationMarkers, canvasDrawInstructions, reactDrawInstructions } =
    usePageDrawInstructions(editor, [page, path], {
      pageHeightPx,
      pageWidthPx,
      rMRemoteSelections,
      rMSelections,
    });

  const memoizedAnnotationMarkers = useDeepCompareMemo(
    () => annotationMarkers,
    [annotationMarkers]
  );

  const groupedAnnotationMarkers = useMemo(
    () =>
      groupAnnotationMarkers({
        annotationMarkers: memoizedAnnotationMarkers,
        editEntries: suggestedEdits,
        editor,
      }),
    [editor, memoizedAnnotationMarkers, suggestedEdits]
  );

  const { canvasRef } = usePageCanvas(canvasDrawInstructions, { pageHeightPx, pageWidthPx });

  return (
    <div ref={setNodeRef} className={className} data-cy={Select.document.editor.pageContent}>
      <canvas ref={canvasRef} />

      {reactDrawInstructions.map(renderReactDrawInstruction)}

      {!isAtVersionHistoryRoute &&
        groupedAnnotationMarkers.map((annotationMarker, index) => (
          <PageAnnotationMarker key={index} {...annotationMarker} />
        ))}
    </div>
  );
}

function renderReactDrawInstruction(drawInstruction: ReactDrawInstruction, index: number) {
  switch (drawInstruction.type) {
    case 'reactComponent': {
      const { key, ...restDrawInstruction } = drawInstruction;
      return <DrawReactComponent key={key} {...restDrawInstruction} />;
    }

    case 'caret':
      return <Caret key={`caret-${index}`} {...drawInstruction} />;

    case 'error':
      return <RenderingError key={drawInstruction.rMPath.toString()} {...drawInstruction} />;
  }
}
